import { Button, Tag } from '@byecode/ui'
import type { EVENT_VARIABLE_TYPE, IndependentAction, NodeFieldContent } from '@lighthouse/core'
import { type DataSourceAbstract } from '@lighthouse/core'
import { type FlowNode, placeholderFromFieldType, useAtomData } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useDataSourceList } from '@/hooks/useDataSource'

import { getDefaultFiledValue } from '../../utils/setNodeRecordContent'
import { AddFieldButton } from './AddFieldButton'
import { FieldItem, SCxHeader, SCxIcon, SCxLabel } from './FieldItem'

interface SettingFieldValueProps {
    appId: string
    envId: string
    value: NodeFieldContent[]
    dataSource: DataSourceAbstract
    upstreamNodes?: FlowNode[]
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    disablePageLink?: boolean
    disableFormOption?: boolean
    disableViewOption?: boolean
    disablePageOption?: boolean
    disableInputOption?: boolean
    eventType?: EVENT_VARIABLE_TYPE
    onChange: (value: NodeFieldContent[]) => void
}

export const SettingFieldValue: React.FC<SettingFieldValueProps> = ({
    appId,
    envId,
    value,
    dataSource,
    upstreamNodes,
    allParentNodes,
    allParentActions,
    disablePageLink,
    disableFormOption,
    disableViewOption,
    disablePageOption,
    disableInputOption,
    eventType,
    onChange
}) => {
    const { schema } = dataSource
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)

    const handleItemClick = useCallback(
        (id: string) => {
            const field = schema[id]
            const defaultValue = getDefaultFiledValue(field.type)
            const newList = [
                ...value,
                {
                    fieldId: field.id,
                    value: defaultValue
                }
            ]
            onChange(newList)
        },
        [onChange, schema, value]
    )

    const handleItemDelete = useCallback(
        (index: number) => {
            const newList = value.filter((_, i) => i !== index)
            onChange(newList)
        },
        [onChange, value]
    )

    const handleItemChange = useCallback(
        (index: number, newValue: NodeFieldContent) => {
            const newList = value.map((item, i) => (i === index ? newValue : item))
            onChange(newList)
        },
        [onChange, value]
    )

    const fieldIds = useMemo(() => value?.map(item => (item).fieldId) ?? [], [value])
    // SYSTEM_FIELD
    return (
        <>
            {value?.map((item, index) => {
                const nodeField = item
                const field = schema[nodeField.fieldId]

                if (!field) {
                    return (
                        <SCxHeader key={nodeField.fieldId}>
                            <SCxLabel>
                                <Tag background="var(--color-red-100)" color="var(--color-red-500)" radius={8}>
                                    找不到字段
                                </Tag>
                            </SCxLabel>
                            <Button
                                type="text"
                                onClick={() => handleItemDelete(index)}
                                icon={<SCxIcon type="Trash" color="var(--color-gray-400)" />}
                            />
                        </SCxHeader>
                    )
                }

                const placeholder = placeholderFromFieldType[field.type]
                return (
                    <FieldItem
                        autoHeight
                        dataSourceList={dataSourceList}
                        workflowList={workflowList}
                        key={nodeField.fieldId}
                        placeholder={placeholder}
                        value={nodeField}
                        allParentNodes={allParentNodes}
                        upstreamNodes={upstreamNodes}
                        allParentActions={allParentActions}
                        field={field}
                        disablePageLink={disablePageLink}
                        disableFormOption={disableFormOption}
                        disableViewOption={disableViewOption}
                        disablePageOption={disablePageOption}
                        disableInputOption={disableInputOption}
                        eventType={eventType}
                        onItemDelete={() => handleItemDelete(index)}
                        onChange={val => handleItemChange(index, val)}
                    />
                )
            })}
            <AddFieldButton dataSource={dataSource} dataSourceList={dataSourceList} fieldIds={fieldIds} onItemClick={handleItemClick} />
        </>
    )
}
